import { v4 } from 'uuid';
import {
  FEATURE_NAME,
  PERSISTED_TABS_MAX_AGE_MS,
  PERSISTED_TABS_MAX_KEYS
} from './terminal-tabs.constant';

const PERSIST_KEY_PREFIX = `${FEATURE_NAME}-`;

export const generatedTabId = () => `tab-${v4()}`;

export const getTerminalTabsPersistKey = (serviceId: string) => `${PERSIST_KEY_PREFIX}${serviceId}`;

// Unreadable or missing `savedAt` reads as 0, i.e. older than any max age.
const readSavedAt = (raw: string | null): number => {
  try {
    const savedAt = JSON.parse(raw ?? '')?.savedAt;
    return Number.isFinite(savedAt) ? savedAt : 0;
  } catch {
    return 0;
  }
};

/**
 * Picks the persisted terminal tabs keys to drop: anything without a readable
 * `savedAt` (corrupt, or written before it existed), anything older than
 * `PERSISTED_TABS_MAX_AGE_MS`, and anything beyond the `PERSISTED_TABS_MAX_KEYS`
 * most recent. `keepKey` (the terminal currently open) is never dropped and
 * counts towards the limit.
 */
export const getStaleTerminalTabsKeys = (
  entries: [ string, string | null ][],
  keepKey: string,
  now: number
) => {
  const candidates = entries
    .filter(([ key ]) => key.startsWith(PERSIST_KEY_PREFIX) && key !== keepKey)
    .map(([ key, raw ]) => ({ key, savedAt: readSavedAt(raw) }));

  const keptKeys = new Set(
    candidates
      .filter(({ savedAt }) => now - savedAt <= PERSISTED_TABS_MAX_AGE_MS)
      .sort((a, b) => b.savedAt - a.savedAt)
      .slice(0, PERSISTED_TABS_MAX_KEYS - 1)
      .map(({ key }) => key)
  );

  return candidates
    .filter(({ key }) => !keptKeys.has(key))
    .map(({ key }) => key);
};

export const prunePersistedTerminalTabs = (keepKey: string, now = Date.now()) => {
  try {
    const entries = Object.keys(localStorage)
      .filter((key) => key.startsWith(PERSIST_KEY_PREFIX))
      .map((key): [ string, string | null ] => [ key, localStorage.getItem(key) ]);

    getStaleTerminalTabsKeys(entries, keepKey, now)
      .forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to prune persisted terminal tabs', e);
  }
};

export const clearPersistedTerminalTabs = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PERSIST_KEY_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear persisted terminal tabs', e);
  }
};
